import React, { useState, useRef, useEffect } from "react";
import clsx from "clsx";

import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowDown from "../../../img/arrow-down.svg";

import "./formField.scss";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const FormField = ({
  id,
  name,
  title,
  type = "text",
  value = "",
  onChange,
  placeholder = "",
  disabled = false,
  required = false,
  icon,
  options = [],
  isFlat = false,
  readOnly = false,
  maxLength,
  searchable = false,
  onSearch,
  searchText = "",
  loading = false,
  viewPassword = false,
}) => {
  const charCount = value ? value.length : 0;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [internalSearchText, setInternalSearchText] = useState(searchText);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const debouncedSearchText = useDebounce(internalSearchText, 400);

  useEffect(() => {
    if (searchable && onSearch && debouncedSearchText !== searchText) {
      onSearch(debouncedSearchText);
    }
  }, [debouncedSearchText, searchable, onSearch, searchText]);

  const isPlaceholderSelected = React.useMemo(() => {
    if (type === "select" && options.length > 0) {
      const selectedOption = options.find((option) => option.value === value);
      return selectedOption?.isPlaceholder || false;
    }
    return false;
  }, [type, options, value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mouseup", handleClickOutside);
      return () => {
        document.removeEventListener("mouseup", handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    setInternalSearchText(searchText);
  }, [searchText]);

  const handleTextareaChange = (e) => {
    if (type === "textarea" && e.target.value.length > 300) {
      return;
    }
    onChange(e);
  };

  const handleSearchableInputChange = (e) => {
    const searchValue = e.target.value;
    setInternalSearchText(searchValue);
  };

  const handleOptionSelect = (optionValue, optionLabel) => {
    const syntheticEvent = {
      target: {
        name: name,
        value: optionValue,
      },
    };

    onChange(syntheticEvent);
    setInternalSearchText("");
    setIsDropdownOpen(false);
  };

  const handleDropdownToggle = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!disabled && !readOnly) {
      const newState = !isDropdownOpen;
      setIsDropdownOpen(newState);

      if (newState) {
        setTimeout(() => {
          const searchInput = dropdownRef.current?.querySelector(
            ".dropdown-search-input"
          );
          if (searchInput) {
            searchInput.focus();
          }
        }, 50);
      }
    }
  };

  const getDisplayValue = () => {
    if (searchable && type === "select") {
      const selectedOption = options.find((option) => option.value === value);
      return selectedOption && !selectedOption.isPlaceholder
        ? selectedOption.label
        : "";
    }
    return value;
  };

  const resolvedInputType =
    type === "password" && viewPassword && isPasswordVisible ? "text" : type;

  const renderInput = () => {
    const commonProps = {
      id,
      name,
      value,
      onChange: type === "textarea" ? handleTextareaChange : onChange,
      disabled: disabled || readOnly,
      className: clsx(
        "common-form-input",
        isPlaceholderSelected && "placeholder-selected",
        disabled && "disabled"
      ),
      placeholder,
      required,
      maxLength: type === "textarea" ? 300 : maxLength,
    };

    switch (type) {
      case "select":
        if (searchable) {
          return (
            <div className="searchable-select-container" ref={dropdownRef}>
              <input
                ref={inputRef}
                type="text"
                value={getDisplayValue()}
                onClick={handleDropdownToggle}
                disabled={disabled || readOnly}
                className={clsx(
                  "common-form-input",
                  !value || isPlaceholderSelected ? "placeholder-selected" : "",
                  disabled && "disabled"
                )}
                placeholder={placeholder}
                required={required}
                readOnly={true}
                autoComplete="off"
              />
              {isDropdownOpen && (
                <div className="searchable-dropdown">
                  <div className="dropdown-search-container">
                    <input
                      type="text"
                      value={internalSearchText}
                      onChange={handleSearchableInputChange}
                      className="dropdown-search-input"
                      placeholder="Search options..."
                      autoComplete="off"
                    />
                  </div>
                  <div className="dropdown-options-container">
                    {loading ? (
                      <div className="searchable-option placeholder">
                        Loading options...
                      </div>
                    ) : options.length > 0 ? (
                      options.map((option, index) => (
                        <div
                          key={index}
                          className={`searchable-option ${
                            option.value === value
                              ? "searchable-option-selected"
                              : ""
                          } ${option.isPlaceholder ? "placeholder" : ""}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!option.isPlaceholder) {
                              handleOptionSelect(option.value, option.label);
                            }
                          }}
                        >
                          {option.label}
                        </div>
                      ))
                    ) : (
                      <div className="searchable-option placeholder">
                        No options found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        } else {
          return (
            <>
              <select {...commonProps}>
                {options.map((option, index) => (
                  <option
                    key={index}
                    value={option.value}
                    disabled={option.isPlaceholder}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </>
          );
        }
      case "textarea":
        return (
          <>
            <textarea {...commonProps} />
            <div className="char-count">{charCount}/300</div>
          </>
        );
      case "date":
        return (
          <>
            <input {...commonProps} type="date" />
            <div className="custom-calendar-icon" />
          </>
        );
      default:
        return <input {...commonProps} type={resolvedInputType} />;
    }
  };

  return (
    <div
      className={clsx(
        "common-form-item",
        isFlat && "flat",
        disabled && "disabled"
      )}
    >
      <label htmlFor={id} className="common-form-label">
        {title}
        {required && <span style={{ color: "#362B73" }}> *</span>}
      </label>
      <div className="common-form-input-wrapper">
        {icon && <img src={icon} alt="" className="input-icon" />}
        {renderInput()}
        {type === "select" && !searchable && (
          <img src={ArrowDown} alt="arrow" className="select-arrow" />
        )}
        {type === "select" && searchable && (
          <img
            src={ArrowDown}
            alt="arrow"
            className={`select-arrow ${isDropdownOpen ? "rotated" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDropdownToggle(e);
            }}
          />
        )}
        {type === "password" && viewPassword && (
          <button
            type="button"
            className="password-toggle-button"
            onClick={() => setIsPasswordVisible((prev) => !prev)}
            disabled={disabled || readOnly}
            aria-label={isPasswordVisible ? "Hide password" : "Show password"}
          >
            {isPasswordVisible ? (
              <VisibilityOffIcon className="password-toggle-icon" />
            ) : (
              <VisibilityIcon className="password-toggle-icon" />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default FormField;
